import React, { useState, useEffect } from "react";
import { API_BASE } from "../apiConfig";
import { useNavigate, useLocation } from "react-router-dom";
import { acceptTermsOfUse, updateUser } from "../utils/api";
import "./SetupAccount.css";
// import { createLocation, updateLocation } from '../utils/api'; // kept for future enhancements

const SetupAccount = () => {
  const navigate = useNavigate();
  const location = useLocation(); // Hook to access URL query params
  const [step, setStep] = useState(1); // 1: Change Password, 2: Create Location
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [termsRequired, setTermsRequired] = useState(false);
  const [termsChecked, setTermsChecked] = useState(false);
  const [termsSaving, setTermsSaving] = useState(false);
  const [termsError, setTermsError] = useState("");

  // Email state (prefilled from URL if present)
  const [email, setEmail] = useState("");

  // Step 1: Password State
  const [currentPassword, setCurrentPassword] = useState("");

  // Extract token/email from URL
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const tokenFromUrl = params.get("token");
    const emailFromUrl = params.get("email");

    if (tokenFromUrl) {
      setCurrentPassword(tokenFromUrl);
    }
    if (emailFromUrl) {
      setEmail(emailFromUrl);
    }
    // We might want to auto-fill email or do a background auth check here in a future iteration
    // For now, we rely on the user having clicked the link and landing here.
    // Ideally, we'd have a backend endpoint to exchange this temp token for a session token immediately
    // but for now we pre-fill the "Current Password" field to reduce friction.
  }, [location.search]);

  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Step 2: Location State
  const [locationData, setLocationData] = useState({
    name: "",
    address: "",
    city: "",
    state: "",
    zip: "",
    status: "Active",
  });

  const [user, setUser] = useState(() => {
    try {
      return JSON.parse(localStorage.getItem("user") || "{}");
    } catch (e) {
      return {};
    }
  });
  const [termsPendingVersion, setTermsPendingVersion] = useState(
    () => localStorage.getItem("termsPendingVersion") || "",
  );
  const resolvePostSetupRoute = (nextUser) => {
    if (nextUser?.aiHiringOnly) return "/ai-hiring";
    const featureAccess = Array.isArray(nextUser?.featureAccess)
      ? nextUser.featureAccess.filter(Boolean)
      : [];
    if (featureAccess.includes("agent247")) return "/agent247";
    if (featureAccess.includes("ai_hiring")) return "/ai-hiring";
    return "/";
  };
  useEffect(() => {
    const stored = localStorage.getItem("user");
    const token = localStorage.getItem("token");
    if (!stored || !token) return;
    try {
      const parsed = JSON.parse(stored);
      if (parsed && parsed.mustChangePassword === false) {
        window.location.href = resolvePostSetupRoute(parsed);
      }
    } catch (err) {
      // ignore parse errors
    }
  }, []);

  const shouldSkipLocationSetup = async (nextUser, token, apiUrl) => {
    const role = String(nextUser?.role || "").toLowerCase();
    const accountType = String(nextUser?.accountType || "").toLowerCase();
    const isAdminRole = [
      "admin",
      "owner",
      "full_access",
      "super admin",
    ].includes(role);
    const isCareTeam = accountType === "caregiver" || accountType === "nurse";

    if (!isAdminRole || isCareTeam) return true;

    try {
      const summaryRes = await fetch(
        `${apiUrl}/api/locations/company-summary`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        },
      );
      const summaryData = await summaryRes.json().catch(() => ({}));
      if (summaryRes.ok) {
        return Boolean(summaryData?.hasLocations);
      }
      console.warn("⚠️ [SETUP] Company summary not ok", summaryData);
      return true; // Fail open: don't force location setup
    } catch (err) {
      console.warn("⚠️ [SETUP] Company summary failed", err);
      return true; // Fail open
    }
  };
  useEffect(() => {
    const accountType = String(user?.accountType || "").toLowerCase();
    if (accountType === "caregiver" || accountType === "nurse") {
      setError("Caregivers and nurses must use the mobile app to sign in.");
      navigate("/login?mobileOnly=1", { replace: true });
    }
  }, [navigate, user?.accountType]);

  const calculateStrength = (password) => {
    let score = 0;
    if (password.length >= 8) score++;
    if (/[a-z]/.test(password)) score++;
    if (/[A-Z]/.test(password)) score++;
    if (/[0-9]/.test(password)) score++;
    if (/[^A-Za-z0-9]/.test(password)) score++;
    return Math.min(score, 4);
  };

  const passwordStrength = calculateStrength(newPassword);
  const passwordRuleChecks = {
    minLength: newPassword.length >= 8,
    lowercase: /[a-z]/.test(newPassword),
    uppercase: /[A-Z]/.test(newPassword),
    number: /[0-9]/.test(newPassword),
    special: /[^A-Za-z0-9]/.test(newPassword),
  };

  const handlePasswordSubmit = async (e) => {
    e.preventDefault();

    // Validate email
    if (!email) {
      setError("Email is required");
      return;
    }

    // Validate current password
    if (!currentPassword) {
      setError("Please enter your temporary password");
      return;
    }

    // Validate new passwords match
    if (newPassword !== confirmPassword) {
      setError("New passwords do not match");
      return;
    }

    // Validate new password strength
    if (
      newPassword.length < 8 ||
      !/[a-z]/.test(newPassword) ||
      !/[A-Z]/.test(newPassword) ||
      !/[0-9]/.test(newPassword)
    ) {
      setError(
        "Password must be at least 8 characters and include 1 lowercase letter, 1 uppercase letter, and 1 number.",
      );
      return;
    }

    // Prevent using same password
    if (currentPassword === newPassword) {
      setError("New password must be different from temporary password");
      return;
    }

    setLoading(true);
    setError("");

    try {
      console.log("🔐 [SETUP] Logging in with temp password for:", email);

      const apiUrl = API_BASE;

      // 1) Login with temp password to get a valid token
      const loginRes = await fetch(`${apiUrl}/api/auth/login`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email,
          password: currentPassword,
          isSetupFlow: true,
          client: "web",
        }),
      });

      const loginData = await loginRes.json().catch(() => ({}));

      if (!loginRes.ok) {
        console.error("❌ [SETUP] Login failed:", loginData);
        throw new Error(
          loginData.message ||
            "Login failed. Please check your temporary password.",
        );
      }

      // Save token/user for subsequent requests and UI
      localStorage.setItem("token", loginData.token);
      if (loginData.refreshToken) {
        localStorage.setItem("refreshToken", loginData.refreshToken);
        const ninetyDaysMs = 90 * 24 * 60 * 60 * 1000;
        localStorage.setItem(
          "refreshTokenExpiresAt",
          String(Date.now() + ninetyDaysMs),
        );
      }
      localStorage.setItem("user", JSON.stringify(loginData.user));
      setUser(loginData.user);
      const mustAcceptTerms =
        loginData.mustAcceptTerms || loginData.user?.mustAcceptTerms;
      const termsVersion =
        loginData.termsVersion || loginData.user?.termsVersion || "";
      if (mustAcceptTerms) {
        localStorage.setItem("termsPendingVersion", termsVersion);
        setTermsPendingVersion(termsVersion);
      } else {
        localStorage.removeItem("termsPendingVersion");
        setTermsPendingVersion("");
      }

      console.log("✅ [SETUP] Logged in, updating password");

      // 2) Update User Password using dedicated change-password endpoint
      const token = loginData.token;
      const response = await fetch(`${apiUrl}/api/auth/change-password`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`, // 🔐 CRITICAL: Send auth token
        },
        body: JSON.stringify({
          currentPassword: currentPassword, // 🔐 CRITICAL: Verify current password
          newPassword: newPassword,
        }),
      });

      const data = await response.json().catch(() => ({}));

      if (!response.ok) {
        console.error("❌ [SETUP] Password update failed:", data);
        throw new Error(data.message || "Failed to update password");
      }

      console.log("✅ [SETUP] Password updated successfully");

      const nextUser = loginData.user;
      if (termsPendingVersion) {
        setTermsRequired(true);
        return;
      }
      const params = new URLSearchParams(location.search);
      const allowLocationSetup = params.get("forceLocationSetup") === "1";
      const skipLocationSetup = await shouldSkipLocationSetup(
        nextUser,
        token,
        apiUrl,
      );

      if (skipLocationSetup || !allowLocationSetup) {
        // Default: go straight to dashboard after password change
        const updatedUser = {
          ...nextUser,
          mustChangePassword: false,
        };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        window.location.href = resolvePostSetupRoute(updatedUser);
        return;
      }

      setStep(2); // Only when explicitly forced
    } catch (err) {
      console.error("🚨 [SETUP] Error:", err);
      setError(err.message || "Failed to update password");
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptTerms = async () => {
    if (!termsChecked) {
      setTermsError("You must accept the Terms of Use to continue.");
      return;
    }
    const userId = user?.id || user?._id;
    if (!userId) {
      setTermsError("User session not found. Please sign in again.");
      return;
    }
    try {
      setTermsSaving(true);
      setTermsError("");
      const updated = await acceptTermsOfUse({
        termsVersion: termsPendingVersion || null,
      });
      const mergedUser = { ...user, ...updated };
      setUser(mergedUser);
      localStorage.setItem("user", JSON.stringify(mergedUser));
      localStorage.removeItem("termsPendingVersion");
      setTermsRequired(false);
      window.location.href = resolvePostSetupRoute(mergedUser);
    } catch (err) {
      setTermsError(err.message || "Failed to record Terms acceptance.");
    } finally {
      setTermsSaving(false);
    }
  };

  useEffect(() => {
    const maybeSkipLocationSetup = async () => {
      if (step !== 2) return;
      const params = new URLSearchParams(location.search);
      const allowLocationSetup = params.get("forceLocationSetup") === "1";
      if (!allowLocationSetup) {
        window.location.href = "/";
        return;
      }
      const apiUrl = API_BASE;
      const token = localStorage.getItem("token");
      if (!token) return;

      const skip = await shouldSkipLocationSetup(user, token, apiUrl);
      if (skip) {
        const updatedUser = {
          ...user,
          mustChangePassword: false,
        };
        setUser(updatedUser);
        localStorage.setItem("user", JSON.stringify(updatedUser));
        window.location.href = resolvePostSetupRoute(updatedUser);
      }
    };
    maybeSkipLocationSetup();
  }, [step, user, location.search]);

  const handleLocationSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");

    try {
      // Create Location
      const apiUrl = API_BASE;
      const token = localStorage.getItem("token");

      const response = await fetch(`${apiUrl}/api/locations`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify(locationData),
      });

      if (!response.ok) throw new Error("Failed to create location");

      const newLocation = await response.json();

      // The backend now auto-links location to user, but we still update localStorage
      const updatedLocations = user.locations
        ? [...user.locations, newLocation._id || newLocation.id]
        : [newLocation._id || newLocation.id];

      await fetch(`${apiUrl}/api/users/${user.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          ...(token && { Authorization: `Bearer ${token}` }),
        },
        body: JSON.stringify({ locations: updatedLocations }),
      });

      // Update local storage user to reflect changes (remove mustChangePassword flag effectively)
      const updatedUser = {
        ...user,
        mustChangePassword: false,
        locations: updatedLocations,
      };
      setUser(updatedUser);
      localStorage.setItem("user", JSON.stringify(updatedUser));

      // Force full page reload to trigger App.js to fetch the new locations
      window.location.href = "/";
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="setup-container">
      <div className="setup-box">
        <div className="setup-logo-container">
          <img src="/new-logo.png" alt="Albert AI" className="setup-logo" />
        </div>
        <div className="setup-header">
          <h2>Setup Your Account</h2>
          <p>
            Welcome, {user?.name || email || "New user"}! Let's get you started.
          </p>
        </div>

        <div className="steps-indicator">
          <div className={`step-dot ${step >= 1 ? "active" : ""}`}>1</div>
          <div className={`step-line ${step >= 2 ? "active" : ""}`}></div>
          <div className={`step-dot ${step >= 2 ? "active" : ""}`}>2</div>
        </div>

        {error && <div className="setup-error">{error}</div>}

        {step === 1 && (
          <form onSubmit={handlePasswordSubmit} className="setup-form">
            <h3>Change Temporary Password</h3>
            <p className="step-desc">
              Enter the temporary password from your email, then choose a new
              secure password.
            </p>

            <div className="form-group">
              <label>Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="you@example.com"
              />
            </div>
            <div className="form-group">
              <label>Temporary Password (from email)</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showCurrentPassword ? "text" : "password"}
                  value={currentPassword}
                  onChange={(e) => setCurrentPassword(e.target.value)}
                  required
                  placeholder="Enter temporary password"
                  style={{ paddingRight: "40px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "18px",
                    color: "#64748b",
                  }}
                  aria-label={
                    showCurrentPassword ? "Hide password" : "Show password"
                  }
                >
                  {showCurrentPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>

            <div className="form-group">
              <label>New Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showNewPassword ? "text" : "password"}
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  required
                  minLength={8}
                  autoComplete="new-password"
                  style={{ paddingRight: "40px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowNewPassword(!showNewPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "18px",
                    color: "#64748b",
                  }}
                  aria-label={
                    showNewPassword ? "Hide password" : "Show password"
                  }
                >
                  {showNewPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
              <div className="password-rules-hint">
                <div className="password-rules-title">Password requirements</div>
                <ul className="password-rules-list">
                  <li
                    className={
                      passwordRuleChecks.minLength
                        ? "password-rule-met"
                        : "password-rule-unmet"
                    }
                  >
                    At least 8 characters
                  </li>
                  <li
                    className={
                      passwordRuleChecks.lowercase
                        ? "password-rule-met"
                        : "password-rule-unmet"
                    }
                  >
                    At least 1 lowercase letter (a-z)
                  </li>
                  <li
                    className={
                      passwordRuleChecks.uppercase
                        ? "password-rule-met"
                        : "password-rule-unmet"
                    }
                  >
                    At least 1 uppercase letter (A-Z)
                  </li>
                  <li
                    className={
                      passwordRuleChecks.number
                        ? "password-rule-met"
                        : "password-rule-unmet"
                    }
                  >
                    At least 1 number (0-9)
                  </li>
                  <li
                    className={
                      passwordRuleChecks.special
                        ? "password-rule-met"
                        : "password-rule-unmet"
                    }
                  >
                    Special character (optional, recommended)
                  </li>
                </ul>
              </div>
              {newPassword && (
                <div style={{ marginTop: "5px", fontSize: "12px" }}>
                  <div
                    style={{ display: "flex", gap: "2px", marginBottom: "4px" }}
                  >
                    {[1, 2, 3, 4].map((i) => (
                      <div
                        key={i}
                        style={{
                          flex: 1,
                          height: "4px",
                          borderRadius: "2px",
                          backgroundColor:
                            i <= passwordStrength
                              ? ["#ef4444", "#f59e0b", "#10b981", "#059669"][
                                  passwordStrength - 1
                                ]
                              : "#e2e8f0",
                        }}
                      />
                    ))}
                  </div>
                  <div style={{ color: "#64748b" }}>
                    {passwordStrength < 3
                      ? "Weak"
                      : passwordStrength < 4
                        ? "Good"
                        : "Strong"}
                  </div>
                </div>
              )}
            </div>
            <div className="form-group">
              <label>Confirm Password</label>
              <div style={{ position: "relative" }}>
                <input
                  type={showConfirmPassword ? "text" : "password"}
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  required
                  style={{ paddingRight: "40px" }}
                />
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  style={{
                    position: "absolute",
                    right: "10px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    fontSize: "18px",
                    color: "#64748b",
                  }}
                  aria-label={
                    showConfirmPassword ? "Hide password" : "Show password"
                  }
                >
                  {showConfirmPassword ? "👁️" : "👁️‍🗨️"}
                </button>
              </div>
            </div>
            <button type="submit" className="setup-btn" disabled={loading}>
              {loading ? "Updating..." : "Next: Create Location"}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleLocationSubmit} className="setup-form">
            <h3>Create Your First Location</h3>
            <p className="step-desc">
              Set up the primary location for your organization.
            </p>

            <div className="form-group">
              <label>Location Name (e.g. HQ, Main Office)</label>
              <input
                type="text"
                value={locationData.name}
                onChange={(e) =>
                  setLocationData({ ...locationData, name: e.target.value })
                }
                required
              />
            </div>
            <div className="form-group">
              <label>Address</label>
              <input
                type="text"
                value={locationData.address}
                onChange={(e) =>
                  setLocationData({ ...locationData, address: e.target.value })
                }
                required
              />
            </div>
            <div className="form-row">
              <div className="form-group">
                <label>City</label>
                <input
                  type="text"
                  value={locationData.city}
                  onChange={(e) =>
                    setLocationData({ ...locationData, city: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>State</label>
                <input
                  type="text"
                  value={locationData.state}
                  onChange={(e) =>
                    setLocationData({ ...locationData, state: e.target.value })
                  }
                  required
                />
              </div>
              <div className="form-group">
                <label>Zip Code</label>
                <input
                  type="text"
                  value={locationData.zip}
                  onChange={(e) =>
                    setLocationData({ ...locationData, zip: e.target.value })
                  }
                  required
                />
              </div>
            </div>

            <button type="submit" className="setup-btn" disabled={loading}>
              {loading ? "Creating..." : "Complete Setup"}
            </button>
          </form>
        )}
      </div>
      {termsRequired && (
        <div className="modal-overlay">
          <div className="modal-content" style={{ maxWidth: 520 }}>
            <h3 style={{ marginTop: 0 }}>Terms of Use</h3>
            <p style={{ color: "#475569" }}>
              Please review and accept the Terms of Use to continue.
            </p>
            <label
              style={{
                display: "flex",
                gap: "10px",
                alignItems: "flex-start",
                marginTop: "16px",
              }}
            >
              <input
                type="checkbox"
                checked={termsChecked}
                onChange={(e) => setTermsChecked(e.target.checked)}
              />
              <span>
                I have read, fully understand, and accept the Terms of Use for
                ALBERTai, a Unity Global Care company. Skip this prompt on
                future logins unless the Terms of Use change.
              </span>
            </label>
            <div style={{ marginTop: "12px" }}>
              <a
                href="/admin/terms-of-use"
                target="_blank"
                rel="noopener noreferrer"
              >
                View Terms of Use
              </a>
            </div>
            {termsError && (
              <div className="setup-error" style={{ marginTop: "12px" }}>
                {termsError}
              </div>
            )}
            <div
              className="modal-actions"
              style={{ justifyContent: "flex-end", marginTop: "20px" }}
            >
              <button
                className="create-button active"
                onClick={handleAcceptTerms}
                disabled={termsSaving}
              >
                {termsSaving ? "Saving..." : "Accept & Continue"}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SetupAccount;
